import React from 'react';
import { Role, ROLE_OPTIONS } from '../types/member-roles';

export interface RoleRadioGroupProps {
  /** The persisted role currently stored in the database */
  currentRole: Role;
  /** The currently selected role (may differ from currentRole if there's a pending change) */
  selectedRole: Role;
  /** Callback when a different role is selected */
  onChange: (role: Role) => void;
  /** Whether the radio buttons should be disabled (e.g., during save) */
  disabled?: boolean;
  /** Whether to show the Admin role option (only Admin users should see it) */
  showAdminOption?: boolean;
  /** Unique identifier for this radio group (prevents HTML name collisions across rows) */
  groupId?: string;
}

/**
 * Renders a group of radio buttons for role selection on a single member row.
 * Visually distinguishes the persisted role from a pending selection.
 */
export function RoleRadioGroup({
  currentRole,
  selectedRole,
  onChange,
  disabled = false,
  showAdminOption = false,
  groupId,
}: RoleRadioGroupProps) {
  const hasPendingChange = selectedRole !== currentRole;

  const visibleOptions = showAdminOption
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((option) => option.value !== 'Admin');

  return (
    <div
      style={{
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
      }}
      role="radiogroup"
      aria-label="Role selection"
    >
      {visibleOptions.map((option) => {
        const isSelected = option.value === selectedRole;
        const isPersisted = option.value === currentRole;

        return (
          <label
            key={option.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              fontSize: '0.85rem',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected && hasPendingChange ? '#c9a84c' : '#0f2d1f',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              backgroundColor:
                isPersisted && !hasPendingChange
                  ? '#e8f5e9'
                  : isSelected && hasPendingChange
                    ? '#fff8e1'
                    : 'transparent',
              border:
                isPersisted && !hasPendingChange
                  ? '1px solid #1a6b3c'
                  : isSelected && hasPendingChange
                    ? '1px solid #c9a84c'
                    : '1px solid transparent',
              transition: 'all 0.15s ease',
            }}
          >
            <input
              type="radio"
              name={`role-${groupId || currentRole}-group`}
              value={option.value}
              checked={isSelected}
              disabled={disabled}
              onChange={() => onChange(option.value)}
              style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
              aria-label={option.label}
            />
            <span>{option.label}</span>
            {isPersisted && hasPendingChange && (
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#666',
                  marginLeft: '0.15rem',
                }}
                title="Currently persisted role"
              >
                (current)
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}
